import { computed } from 'vue';
import {
  calcSymbolBuyValue,
  calcSymbolSellValue,
  transformTradePriceChange,
  SYMBOL_TRADE_CHANNEL_PREFIX,
  useSymbolDeductions,
  TransformTradePriceChangeDataType,
  calcSymbolBreakPointPrice,
} from '@core/symbol';
import { useLiveQueryV2 } from '@libs/use';
import { VirtualPortfolioQuery } from '@libs/api-reference';
import { CODES } from '@libs/use';
import { MaybeRef } from '@vueuse/core';

import * as API from '../../requests';
import { calcPortfolioReturnValuePercentage } from '../../utils/calculator';
import { PORTFOLIO_QUERY_KEYS } from '../../constants';

export default function useLiveVirtualPortfolioQuery(
  {
    listen,
    immediate,
  }: {
    listen?: boolean;
    immediate: MaybeRef<boolean>;
  } = { listen: true, immediate: false }
) {
  const { findWageConfigBySymbol, deductions } = useSymbolDeductions();

  const {
    result,
    execute: getVirtualPortfolio,
    isIdle: isWaitingForVirtualPortfolioSymbols,
    isPending: isLoadingVirtualPortfolioSymbols,
    isRevalidating: isRevalidatingVirtualPortfolioSymbols,
    isError: isErrorVirtualPortfolioSymbols,
    isSuccess: isSuccessVirtualPortfolioSymbols,
  } = useLiveQueryV2<VirtualPortfolioQuery>({
    key: PORTFOLIO_QUERY_KEYS.virtualPortfolio,
    fetcher: API.getVirtualPortfolio,
    immediate,
    getChannels: listen
      ? (result) => {
          const symbols = result?.data.items || [];

          return symbols?.reduce((symbolsChannels: string[], currentSymbol) => {
            symbolsChannels.push(
              `${SYMBOL_TRADE_CHANNEL_PREFIX}${currentSymbol.instrumentIsin}`
            );

            return symbolsChannels;
          }, []);
        }
      : () => [],
    onUpdate(signal) {
      const actions = {
        [CODES.SymbolTradePriceChangeSignal]: transformTradePriceChange,
      };

      const resultData = result.value?.data as VirtualPortfolioQuery;

      if (actions[signal.code] && resultData?.items) {
        const data = actions[signal.code](
          signal.data as TransformTradePriceChangeDataType
        );

        const index = resultData.items.findIndex(
          (s) => s.instrumentIsin === data.isin
        );

        const item = resultData.items[index];

        if (index > -1 && item.lastTradePrice !== data.lastTradePrice) {
          Object.assign(item, data);
        }
      }
    },
  });

  const symbols = computed(() => {
    const resultData = result.value?.data as VirtualPortfolioQuery;

    if (!deductions.value?.length || !resultData?.items?.length) {
      return [];
    }

    return resultData.items
      .map((symbol) => {
        const wage = findWageConfigBySymbol(symbol);
        const flooredNetAveragePrice = Math.floor(symbol.averagePrice);

        if (wage) {
          const symbolSellValue = calcSymbolSellValue(
            { quantity: symbol.quantity, price: symbol.lastTradePrice },
            wage
          );

          const symbolBuyValue = calcSymbolBuyValue(
            { quantity: symbol.quantity, price: flooredNetAveragePrice },
            wage
          );

          const breakPoint = calcSymbolBreakPointPrice(
            flooredNetAveragePrice,
            0,
            wage
          );

          const returnValue = symbolSellValue - symbolBuyValue;

          return {
            ...symbol,
            value: symbolSellValue,
            buyTotalTradeVolume: symbolBuyValue,
            netAveragePrice: flooredNetAveragePrice,
            returnValue,
            returnValuePercentage: calcPortfolioReturnValuePercentage(
              returnValue,
              symbolBuyValue
            ),
            symbolBuyValue,
            breakPoint,
          };
        }

        return {
          ...symbol,
          netAveragePrice: flooredNetAveragePrice,
          value: 0,
          returnValue: 0,
        };
      })
      .sort((a, b) => b.value - a.value);
  });

  const virtualPortfolioStats = computed(() => {
    const defaultStats = {
      sumValues: 0,
      sumReturnValues: 0,
      sumReturnValuesPercentage: 0,
      sumBuyTotalTradeVolumes: 0,
    };

    if (!symbols.value?.length) {
      return defaultStats;
    }

    const { sumValues, sumReturnValues, sumBuyTotalTradeVolumes } =
      symbols.value.reduce(
        (total, symbol) => {
          total.sumBuyTotalTradeVolumes += symbol.buyTotalTradeVolume;
          total.sumReturnValues += symbol.returnValue;
          total.sumValues += symbol.value;

          return total;
        },
        { sumValues: 0, sumReturnValues: 0, sumBuyTotalTradeVolumes: 0 }
      );

    return {
      sumValues,
      sumReturnValues,
      sumReturnValuesPercentage: calcPortfolioReturnValuePercentage(
        sumReturnValues,
        sumBuyTotalTradeVolumes
      ),
      sumBuyTotalTradeVolumes,
    };
  });

  return {
    symbols,
    isRevalidatingVirtualPortfolioSymbols,
    isErrorVirtualPortfolioSymbols,
    isSuccessVirtualPortfolioSymbols,
    getVirtualPortfolio,
    virtualPortfolioStats,
    isLoadingVirtualPortfolioSymbols,
    isWaitingForVirtualPortfolioSymbols,
  };
}
